import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Chip,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  useTheme,
  Switch,
  FormControlLabel,
  Divider,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  Container
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  Print as PrintIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  Psychology as PsychologyIcon,
  Note as NoteIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  SentimentVeryDissatisfied,
  SentimentDissatisfied,
  SentimentNeutral,
  SentimentSatisfied,
  SentimentVerySatisfied,
  ArrowBack
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { experienciaService, usuarioService, clienteService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// =====================================================
// TIPOS E INTERFACES
// =====================================================

interface Experiencia {
  id: number;
  id_usuario: number; // Mudança: era usuario_id
  usuario_nome?: string;
  cliente_nome?: string;
  id_cliente: number; // Mudança: era cliente_id
  criado_em: string; // Mudança: era data_experiencia
  meu_humor: string; // Mudança: de number para string
  experiencia_positiva: string;
  experiencia_negativa: string;
  // Remover campos que não existem na tabela:
  // ativo: boolean;
  // created_at?: string;
  // updated_at?: string;
}

interface ExperienciaFormData {
  usuario_id: string;
  data: string;
  titulo: string;
  descricao: string;
  observacoes: string;
  tipo: string;
  ativo: boolean;
}

interface Usuario {
  id: number;
  nome: string;
  email: string;
}

interface Cliente {
  id: number;
  nome: string;
}

interface DeleteDialogState {
  open: boolean;
  id: number | null;
}

interface FilterState {
  cliente: string;
  periodo: string;
  humor: string;
}

// =====================================================
// CONSTANTES DE HUMOR
// =====================================================

const humorOptions = [
  { value: 1, icon: SentimentVeryDissatisfied, label: 'Muito Triste', color: '#f44336' },
  { value: 2, icon: SentimentDissatisfied, label: 'Triste', color: '#ff9800' },
  { value: 3, icon: SentimentNeutral, label: 'Neutro', color: '#9e9e9e' },
  { value: 4, icon: SentimentSatisfied, label: 'Feliz', color: '#4caf50' },
  { value: 5, icon: SentimentVerySatisfied, label: 'Muito Feliz', color: '#2196f3' }
];

// Função para obter o ícone do humor
// Função para converter humor de texto para número
const convertHumorToNumber = (humorText: string): number => {
  const humorMap: { [key: string]: number } = {
    'muito_triste': 1,
    'muito triste': 1,
    'triste': 2,
    'neutro': 3,
    'feliz': 4,
    'muito_feliz': 5,
    'muito feliz': 5
  };
  
  const lowerText = humorText.toLowerCase().trim();
  return humorMap[lowerText] || 3; // Default para neutro se não encontrar
};

const getHumorIcon = (humorValue: string) => {
  const numericValue = convertHumorToNumber(humorValue);
  const option = humorOptions.find(opt => opt.value === numericValue);
  if (!option) return null;
  const IconComponent = option.icon;
  return (
    <Tooltip title={option.label}>
      <IconComponent sx={{ color: option.color, fontSize: 30 }} />
    </Tooltip>
  );
};

// =====================================================
// FUNÇÕES DE FORMATAÇÃO E VALIDAÇÃO
// =====================================================

const formatarData = (data: string): string => {
  if (!data) return 'Data não informada';
  
  try {
    // Tentar diferentes formatos de data
    const dataObj = new Date(data);
    
    // Verificar se a data é válida
    if (isNaN(dataObj.getTime())) {
      return 'Data inválida';
    }
    
    return dataObj.toLocaleDateString('pt-BR');
  } catch (error) {
    console.error('Erro ao formatar data:', error, 'Data recebida:', data);
    return 'Erro na data';
  }
};

const formatarDataHora = (data: string): string => {
  console.log('🔍 [DEBUG] formatarDataHora recebeu:', data, 'tipo:', typeof data);
  
  if (!data) {
    console.log('❌ [DEBUG] Data está vazia ou null');
    return 'Data não informada';
  }
  
  try {
    // Tentar diferentes formatos de data
    let dataObj;
    
    // Se for uma string, tentar converter
    if (typeof data === 'string') {
      // Verificar se já está no formato ISO
      if (data.includes('T')) {
        dataObj = new Date(data);
      } else if (data.includes('-')) {
        // Formato YYYY-MM-DD HH:mm:ss
        dataObj = new Date(data.replace(' ', 'T'));
      } else {
        dataObj = new Date(data);
      }
    } else {
      dataObj = new Date(data);
    }
    
    console.log('🔍 [DEBUG] Data convertida:', dataObj);
    
    // Verificar se a data é válida
    if (isNaN(dataObj.getTime())) {
      console.log('❌ [DEBUG] Data inválida após conversão');
      return 'Data inválida';
    }
    
    // Retornar data e hora formatadas
    const resultado = dataObj.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    console.log('✅ [DEBUG] Data formatada:', resultado);
    return resultado;
    
  } catch (error) {
    console.error('❌ [DEBUG] Erro ao formatar data:', error, 'Data recebida:', data);
    return 'Erro na data';
  }
};

const validarData = (data: string): boolean => {
  if (!data) return false;
  const dataObj = new Date(data);
  return !isNaN(dataObj.getTime());
};

const validarCamposObrigatorios = (formData: ExperienciaFormData): Record<string, string> => {
  const erros: Record<string, string> = {};
  
  if (!formData.usuario_id) {
    erros.usuario_id = 'Usuário é obrigatório';
  }
  
  if (!formData.data) {
    erros.data = 'Data é obrigatória';
  } else if (!validarData(formData.data)) {
    erros.data = 'Data inválida';
  }
  
  if (!formData.titulo?.trim()) {
    erros.titulo = 'Título é obrigatório';
  }
  
  if (!formData.descricao?.trim()) {
    erros.descricao = 'Descrição é obrigatória';
  }
  
  if (!formData.tipo?.trim()) {
    erros.tipo = 'Tipo é obrigatório';
  }
  
  return erros;
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const Experiencias: React.FC = () => {
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate(); // Corrigido: mover para dentro do componente
  
  // =====================================================
  // ESTADOS PRINCIPAIS
  // =====================================================
  const [experiencias, setExperiencias] = useState<Experiencia[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  
  // Estados do formulário
  const [formData, setFormData] = useState<ExperienciaFormData>({
    usuario_id: '',
    data: new Date().toISOString().split('T')[0],
    titulo: '',
    descricao: '',
    observacoes: '',
    tipo: '',
    ativo: true
  });
  
  // Estados de controle
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filteredExperiencias, setFilteredExperiencias] = useState<Experiencia[]>([]);
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({ open: false, id: null });
  const [filterState, setFilterState] = useState<FilterState>({
    cliente: '',
    periodo: '',
    humor: ''
  });

  // =====================================================
  // CARREGAR DADOS INICIAIS
  // =====================================================
  useEffect(() => {
    const carregarDadosIniciais = async () => {
      await carregarDados();
    };
    carregarDadosIniciais();
  }, []);

  // Função para visualizar experiência
  const handleVisualizarExperiencia = (id: number) => {
    navigate(`/app/experiencia-ficha/${id}`);
  };

  // Carregar dados se for edição
  useEffect(() => {
    if (id && id !== 'novo') {
      carregarExperiencia(parseInt(id));
    }
  }, [id]);

  // Filtrar experiências baseado no termo de busca
  useEffect(() => {
    console.log('🔍 [DEBUG] useEffect filtro executado:', {
      searchTerm,
      experienciasLength: experiencias.length,
      usuariosLength: usuarios.length
    });
    
    if (searchTerm) {
      const filtered = experiencias.filter((experiencia: Experiencia) => {
        const usuario = usuarios.find((u: Usuario) => u.id === experiencia.id_usuario); // Corrigido: era usuario_id
        const nomeUsuario = usuario ? usuario.nome.toLowerCase() : '';
        const experienciaPositiva = experiencia.experiencia_positiva?.toLowerCase() || '';
        const experienciaNegativa = experiencia.experiencia_negativa?.toLowerCase() || '';
        const dataExperiencia = formatarData(experiencia.criado_em).toLowerCase(); // Corrigido: era data_experiencia
        
        return nomeUsuario.includes(searchTerm.toLowerCase()) ||
               experienciaPositiva.includes(searchTerm.toLowerCase()) ||
               experienciaNegativa.includes(searchTerm.toLowerCase()) ||
               dataExperiencia.includes(searchTerm.toLowerCase());
      });
      console.log('🔍 [DEBUG] Experiências filtradas:', filtered);
      setFilteredExperiencias(filtered);
    } else {
      console.log('🔍 [DEBUG] Sem filtro, usando todas as experiências:', experiencias);
      setFilteredExperiencias(experiencias);
    }
  }, [searchTerm, experiencias, usuarios, loading]);

  // Filtrar experiências baseado nos filtros
  useEffect(() => {
    if (filterState.cliente || filterState.periodo || filterState.humor) {
      const filtered = experiencias.filter((experiencia: Experiencia) => {
        const cliente = clientes.find((c: Cliente) => c.id === experiencia.id_cliente);
        const nomeCliente = cliente ? cliente.nome.toLowerCase() : '';
        const humor = experiencia.meu_humor.toLowerCase();
        
        // Filtro por cliente
        const clienteMatch = filterState.cliente ? nomeCliente.includes(filterState.cliente.toLowerCase()) : true;
        
        // Filtro por humor
        let humorMatch = true;
        if (filterState.humor) {
          // Converter o valor do banco (texto) para número para comparação
          const humorNumericoExperiencia = convertHumorToNumber(experiencia.meu_humor);
          const humorOption = humorOptions.find(opt => opt.label === filterState.humor);
          
          if (humorOption) {
            humorMatch = humorNumericoExperiencia === humorOption.value;
          } else {
            humorMatch = false;
          }
        }
        
        // Filtro por período - implementação corrigida
        let periodoMatch = true;
        if (filterState.periodo) {
          const dataExperiencia = new Date(experiencia.criado_em);
          const hoje = new Date();
          const ontem = new Date(hoje);
          ontem.setDate(hoje.getDate() - 1);
          
          // Zerar horas para comparação apenas de datas
          const hojeData = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
          const ontemData = new Date(ontem.getFullYear(), ontem.getMonth(), ontem.getDate());
          const experienciaData = new Date(dataExperiencia.getFullYear(), dataExperiencia.getMonth(), dataExperiencia.getDate());
          
          switch (filterState.periodo) {
            case 'Hoje':
              periodoMatch = experienciaData.getTime() === hojeData.getTime();
              break;
            case 'Ontem':
              periodoMatch = experienciaData.getTime() === ontemData.getTime();
              break;
            case 'Esta semana':
              const inicioSemana = new Date(hoje);
              inicioSemana.setDate(hoje.getDate() - hoje.getDay());
              inicioSemana.setHours(0, 0, 0, 0);
              periodoMatch = dataExperiencia >= inicioSemana && dataExperiencia <= hoje;
              break;
            case 'Este mês':
              const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
              const fimMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
              fimMes.setHours(23, 59, 59, 999);
              periodoMatch = dataExperiencia >= inicioMes && dataExperiencia <= fimMes;
              break;
            default:
              periodoMatch = true;
          }
        }
        
        return clienteMatch && periodoMatch && humorMatch;
      });
      console.log('🔍 [DEBUG] Experiências filtradas:', filtered);
      setFilteredExperiencias(filtered);
    } else {
      console.log('🔍 [DEBUG] Sem filtro, usando todas as experiências:', experiencias);
      setFilteredExperiencias(experiencias);
    }
  }, [filterState, experiencias, clientes, usuarios, loading]);

  // =====================================================
  // FUNÇÕES PRINCIPAIS
  const carregarDados = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 [DEBUG] Iniciando carregamento de dados...');
      console.log('🔍 [DEBUG] Usuário logado:', user);
      
      const usuarioId = user?.id || 1;

      const [experienciasResponse, clientesResponse] = await Promise.all([
        experienciaService.listar({
          usuario_id: usuarioId,
          limite: 100,
          pagina: 1
        }),
        clienteService.listar({ usuario_id: usuarioId, ativo: 1 })
      ]);

      console.log('✅ [DEBUG] Dados carregados do backend (experiências):', experienciasResponse.data);
      console.log('✅ [DEBUG] Dados carregados do backend (clientes):', clientesResponse.data);
      
      const experienciasDataRaw = Array.isArray(experienciasResponse.data.data) 
        ? experienciasResponse.data.data 
        : [];
      
      const clientesData = Array.isArray(clientesResponse.data) 
        ? clientesResponse.data 
        : Array.isArray(clientesResponse.data.data) 
        ? clientesResponse.data.data 
        : [];
      
      setClientes(clientesData);

      const experienciasData = experienciasDataRaw.map((exp: any) => {
        const clienteRelacionado = clientesData.find(
          (c: any) => Number(c.id) === Number(exp.cliente_id)
        );
        const nomeCliente =
          clienteRelacionado?.nome ||
          exp.cliente_nome ||
          'N/A';

        return {
          id: exp.id,
          id_usuario: exp.usuario_id,
          usuario_nome: exp.usuario_nome,
          cliente_nome: nomeCliente,
          id_cliente: exp.cliente_id,
          criado_em: exp.created_at,
          meu_humor: exp.meu_humor,
          experiencia_positiva: exp.experiencia_positiva,
          experiencia_negativa: exp.experiencia_negativa
        };
      });
      
      experienciasData.sort(
        (a: Experiencia, b: Experiencia) =>
          new Date(b.criado_em).getTime() - new Date(a.criado_em).getTime()
      );
      
      console.log('🔍 [DEBUG] Experiências processadas com nomes de clientes:', {
        total: experienciasData.length,
        dados: experienciasData,
        primeiraExperiencia: experienciasData[0]
      });
      
      setExperiencias(experienciasData);
      
      console.log('✅ [DEBUG] Estados atualizados - Experiências:', experienciasData.length);
      
    } catch (error) {
      console.error('❌ [DEBUG] Erro ao carregar experiências:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      setError(`Erro ao carregar experiências: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const carregarExperiencia = async (experienciaId: number): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      
      const experienciaResponse = await experienciaService.buscarPorId(experienciaId);
      const experiencia = experienciaResponse.data;
      setFormData({
        usuario_id: experiencia.usuario_id.toString(),
        data: experiencia.data.split('T')[0],
        titulo: experiencia.titulo,
        descricao: experiencia.descricao,
        observacoes: experiencia.observacoes || '',
        tipo: experiencia.tipo,
        ativo: experiencia.ativo
      });
      setIsEditing(true);
    } catch (error) {
      console.error('Erro ao carregar experiência:', error);
      setError('Erro ao carregar experiência. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ExperienciaFormData, value: string | boolean): void => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpar erros quando o usuário começa a digitar
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    const erros = validarCamposObrigatorios(formData);
    if (Object.keys(erros).length > 0) {
      setError('Por favor, preencha todos os campos obrigatórios.');
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      
      const dadosParaEnviar = {
        ...formData,
        usuario_id: parseInt(formData.usuario_id)
      };
      
      if (isEditing && id) {
        await experienciaService.atualizar(parseInt(id), dadosParaEnviar);
        setSuccess('Experiência atualizada com sucesso!');
      } else {
        await experienciaService.criar(dadosParaEnviar);
        setSuccess('Experiência criada com sucesso!');
      }
      
      // Limpar formulário e voltar para lista
      setTimeout(() => {
        navigate('/app/experiencias');
      }, 1500);
      
    } catch (error) {
      console.error('Erro ao salvar experiência:', error);
      setError('Erro ao salvar experiência. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteDialog.id) return;
    
    try {
      setLoading(true);
      await experienciaService.excluir(deleteDialog.id);
      setSuccess('Experiência excluída com sucesso!');
      carregarDados();
      setDeleteDialog({ open: false, id: null });
    } catch (error) {
      console.error('Erro ao excluir experiência:', error);
      setError('Erro ao excluir experiência. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (experiencia: Experiencia): void => {
    navigate(`/app/experiencias/editar/${experiencia.id}`);
  };

  const handleNew = (): void => {
    navigate('/app/experiencias/novo');
  };

  const handleCancel = (): void => {
    navigate('/app/experiencias');
  };

  const limparFormulario = (): void => {
    setFormData({
      usuario_id: '',
      data: new Date().toISOString().split('T')[0],
      titulo: '',
      descricao: '',
      observacoes: '',
      tipo: '',
      ativo: true
    });
    setError('');
    setIsEditing(false);
  };

  // =====================================================
  // RENDERIZAÇÃO DO FORMULÁRIO (EDIÇÃO/CRIAÇÃO)
  // =====================================================
  if (id) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Typography 
                variant="h5" 
                component="h1"
                fontWeight="bold"
                color="primary"
                sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' } }}
              >
                {isEditing ? 'Editar Experiência' : 'Nova Experiência'}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Usuário</InputLabel>
                    <Select
                      value={formData.usuario_id}
                      onChange={(e) => handleInputChange('usuario_id', e.target.value)}
                      label="Usuário"
                      disabled={loading}
                    >
                      {usuarios.map((usuario) => (
                        <MenuItem key={usuario.id} value={usuario.id}>
                          {usuario.nome}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Data"
                    type="date"
                    value={formData.data}
                    onChange={(e) => handleInputChange('data', e.target.value)}
                    required
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Título"
                    value={formData.titulo}
                    onChange={(e) => handleInputChange('titulo', e.target.value)}
                    required
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DescriptionIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      value={formData.tipo}
                      onChange={(e) => handleInputChange('tipo', e.target.value)}
                      label="Tipo"
                      disabled={loading}
                    >
                      <MenuItem value="Clínica">Clínica</MenuItem>
                      <MenuItem value="Acadêmica">Acadêmica</MenuItem>
                      <MenuItem value="Pesquisa">Pesquisa</MenuItem>
                      <MenuItem value="Supervisão">Supervisão</MenuItem>
                      <MenuItem value="Outra">Outra</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descrição"
                    value={formData.descricao}
                    onChange={(e) => handleInputChange('descricao', e.target.value)}
                    required
                    multiline
                    rows={4}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PsychologyIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Observações"
                    value={formData.observacoes}
                    onChange={(e) => handleInputChange('observacoes', e.target.value)}
                    multiline
                    rows={3}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <NoteIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.ativo}
                        onChange={(e) => handleInputChange('ativo', e.target.checked)}
                        disabled={loading}
                      />
                    }
                    label="Experiência Ativa"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={saving}
                      startIcon={<CancelIcon />}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={saving}
                      startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                      {saving ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // =====================================================
  // RENDERIZAÇÃO DA LISTA DE EXPERIÊNCIAS
  // =====================================================
  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: '#f5f5f5',
      p: { xs: 0.5, sm: 1, md: 2 }
    }}>
      <Container maxWidth="xl" sx={{ py: { xs: 1, sm: 2, md: 3 } }}>
        <Card 
          elevation={3}
          sx={{ 
            borderRadius: { xs: 0, sm: 2 },
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ 
            p: { xs: 1, sm: 2, md: 3 },
            '&:last-child': { pb: { xs: 1, sm: 2, md: 3 } }
          }}>
            {/* Header */}
            <Box sx={{ 
              mb: { xs: 2, sm: 3 },
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              {/* Botão Voltar */}
              <IconButton
                onClick={() => navigate(-1)}
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main + '10',
                  },
                }}
              >
                <ArrowBack />
              </IconButton>
              
              <Box>
                <Typography 
                  variant="h5" 
                  component="h1"
                  fontWeight="bold"
                  color="primary"
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' } }}
                >
                  Experiências
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mt: 0.5, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  Consulta e gerenciamento de experiências
                </Typography>
              </Box>
            </Box>

            {/* Alerts */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                {error}
              </Alert>
            )}

            {success && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 2,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                {success}
              </Alert>
            )}

            {/* Search and Clear Filters */}
            <Box sx={{ 
              mb: { xs: 2, sm: 3 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 2 },
              alignItems: { xs: 'stretch', sm: 'flex-start' }
            }}>
              <TextField
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                placeholder="Buscar por cliente, data ou experiência..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize={isMobile ? 'small' : 'medium'} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  flex: 1,
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
              <Button
                variant="outlined"
                size={isMobile ? 'small' : 'medium'}
                startIcon={<FilterListIcon fontSize={isMobile ? 'small' : 'medium'} />}
                onClick={() => {
                  setFilterState({
                    cliente: '',
                    periodo: '',
                    humor: ''
                  });
                }}
                sx={{ 
                  minWidth: { xs: '100%', sm: '140px' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  whiteSpace: 'nowrap'
                }}
              >
                Limpar Filtros
              </Button>
            </Box>

            {/* Filters */}
            <Box sx={{ mb: { xs: 2, sm: 3 } }}>
              <Grid container spacing={{ xs: 1, sm: 2 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                    <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Cliente
                    </InputLabel>
                    <Select
                      value={filterState.cliente}
                      onChange={(e) => setFilterState(prev => ({ ...prev, cliente: e.target.value }))}
                      label="Cliente"
                      sx={{
                        '& .MuiSelect-select': {
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }
                      }}
                    >
                      <MenuItem value="">
                        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Todos
                        </Typography>
                      </MenuItem>
                      {clientes.map((cliente) => (
                        <MenuItem key={cliente.id} value={cliente.nome}>
                          <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {cliente.nome}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                    <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Período
                    </InputLabel>
                    <Select
                      value={filterState.periodo}
                      onChange={(e) => setFilterState(prev => ({ ...prev, periodo: e.target.value }))}
                      label="Período"
                      sx={{
                        '& .MuiSelect-select': {
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }
                      }}
                    >
                      <MenuItem value="">
                        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Todos
                        </Typography>
                      </MenuItem>
                      <MenuItem value="Hoje">
                        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Hoje
                        </Typography>
                      </MenuItem>
                      <MenuItem value="Ontem">
                        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Ontem
                        </Typography>
                      </MenuItem>
                      <MenuItem value="Esta semana">
                        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Esta semana
                        </Typography>
                      </MenuItem>
                      <MenuItem value="Este mês">
                        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Este mês
                        </Typography>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={12} md={4}>
                  <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                    <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Humor
                    </InputLabel>
                    <Select
                      value={filterState.humor}
                      onChange={(e) => setFilterState(prev => ({ ...prev, humor: e.target.value }))}
                      label="Humor"
                      sx={{
                        '& .MuiSelect-select': {
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }
                      }}
                    >
                      <MenuItem value="">
                        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Todos
                        </Typography>
                      </MenuItem>
                      {humorOptions.map((option) => (
                        <MenuItem key={option.value} value={option.label}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <option.icon sx={{ 
                              color: option.color, 
                              fontSize: { xs: 16, sm: 20 }, 
                              mr: 1 
                            }} />
                            <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {option.label}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            {/* Table */}
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                minHeight: '200px'
              }}>
                <CircularProgress size={isMobile ? 30 : 40} />
              </Box>
            ) : (
              <TableContainer 
                component={Paper} 
                elevation={1}
                sx={{ 
                  maxHeight: { xs: '60vh', sm: '65vh', md: '70vh' },
                  minHeight: { xs: '300px', sm: '400px', md: '500px' },
                  overflowX: 'auto',
                  overflowY: 'auto',
                  borderRadius: 1,
                  border: '1px solid #e0e0e0'
                }}
              >
                <Table 
                  stickyHeader
                  sx={{ 
                    minWidth: { xs: 280, sm: 500, md: 650 }
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontWeight: 'bold',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          py: { xs: 1, sm: 1.5 },
                          px: { xs: 0.5, sm: 2 },
                          bgcolor: 'grey.50',
                          borderBottom: '2px solid #e0e0e0',
                          minWidth: { xs: 50, sm: 80 }
                        }}
                      >
                        Humor
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 'bold',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          py: { xs: 1, sm: 1.5 },
                          px: { xs: 0.5, sm: 2 },
                          bgcolor: 'grey.50',
                          borderBottom: '2px solid #e0e0e0',
                          minWidth: { xs: 80, sm: 120 }
                        }}
                      >
                        Cliente
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 'bold',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          py: { xs: 1, sm: 1.5 },
                          px: { xs: 0.5, sm: 2 },
                          bgcolor: 'grey.50',
                          borderBottom: '2px solid #e0e0e0',
                          minWidth: { xs: 80, sm: 100 }
                        }}
                      >
                        Data
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          display: { xs: 'none', md: 'table-cell' },
                          fontWeight: 'bold',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          py: { xs: 1, sm: 1.5 },
                          px: { xs: 0.5, sm: 2 },
                          bgcolor: 'grey.50',
                          borderBottom: '2px solid #e0e0e0',
                          minWidth: 150
                        }}
                      >
                        Exp. Positiva
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          display: { xs: 'none', md: 'table-cell' },
                          fontWeight: 'bold',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          py: { xs: 1, sm: 1.5 },
                          px: { xs: 0.5, sm: 2 },
                          bgcolor: 'grey.50',
                          borderBottom: '2px solid #e0e0e0',
                          minWidth: 150
                        }}
                      >
                        Exp. Negativa
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 'bold',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          py: { xs: 1, sm: 1.5 },
                          px: { xs: 0.5, sm: 2 },
                          bgcolor: 'grey.50',
                          borderBottom: '2px solid #e0e0e0',
                          width: { xs: 60, sm: 80 },
                          textAlign: 'center'
                        }}
                      >
                        Ações
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!Array.isArray(filteredExperiencias) || filteredExperiencias.length === 0 ? (
                      <TableRow>
                        <TableCell 
                          colSpan={6} 
                          align="center"
                          sx={{ 
                            py: { xs: 3, sm: 4 },
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          }}
                        >
                          {loading ? (
                            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                              <CircularProgress size={20} />
                              <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                Carregando experiências...
                              </Typography>
                            </Box>
                          ) : error ? (
                            <Typography 
                              color="error"
                              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                            >
                              {error}
                            </Typography>
                          ) : (
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                            >
                              Nenhuma experiência encontrada
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ) : (
                      (filteredExperiencias || []).map((experiencia, index) => (
                        <TableRow
                          key={experiencia.id}
                          sx={{
                            height: '40px',
                            backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' }
                          }}
                        >
                          <TableCell sx={{ 
                            py: 0.5,
                            px: { xs: 0.5, sm: 2 },
                            textAlign: 'center'
                          }}>
                            {getHumorIcon(experiencia.meu_humor)}
                          </TableCell>
                          <TableCell sx={{ 
                            py: 0.5,
                            px: { xs: 0.5, sm: 2 }
                          }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                fontWeight: 500,
                                wordBreak: 'break-word',
                                lineHeight: 1.2
                              }}
                            >
                              {experiencia.cliente_nome || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ 
                            py: 0.5,
                            px: { xs: 0.5, sm: 2 }
                          }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {formatarDataHora(experiencia.criado_em)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ 
                            display: { xs: 'none', md: 'table-cell' },
                            py: 0.5,
                            px: { xs: 0.5, sm: 2 },
                            maxWidth: 200
                          }}>
                            <Typography 
                              variant="body2"
                              sx={{ 
                                fontSize: '0.875rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {experiencia.experiencia_positiva ? 
                                experiencia.experiencia_positiva.substring(0, 30) + 
                                (experiencia.experiencia_positiva.length > 30 ? '...' : '') 
                                : 'N/A'
                              }
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ 
                            display: { xs: 'none', md: 'table-cell' },
                            py: 0.5,
                            px: { xs: 0.5, sm: 2 },
                            maxWidth: 200
                          }}>
                            <Typography 
                              variant="body2"
                              sx={{ 
                                fontSize: '0.875rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {experiencia.experiencia_negativa ? 
                                experiencia.experiencia_negativa.substring(0, 30) + 
                                (experiencia.experiencia_negativa.length > 30 ? '...' : '') 
                                : 'N/A'
                              }
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ 
                            py: 0.5,
                            px: { xs: 0.5, sm: 1 },
                            textAlign: 'center'
                          }}>
                            <IconButton 
                              onClick={() => handleVisualizarExperiencia(experiencia.id)} 
                              color="primary" 
                              size={isMobile ? "small" : "medium"}
                              sx={{ 
                                p: { xs: 0.5, sm: 1 }
                              }}
                            >
                              <VisibilityIcon sx={{ 
                                fontSize: { xs: '1rem', sm: '1.25rem' } 
                              }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Experiencias;
